/**
 * QCC-PAGINATE-001 (Feb 2026) — Quality Command Center pagination strip.
 *
 * Renders:
 *   ┌──────────────────────────────────────────────────────────────────┐
 *   │  Showing 1–25 of 342 records · Page 1 of 14                      │
 *   │                              Rows per page: [25 ▾]  ‹ Prev  Next ›│
 *   └──────────────────────────────────────────────────────────────────┘
 *
 * Props
 *   - page            (number, 1-indexed)
 *   - perPage         (25 | 50 | 100)
 *   - totalCount      (number, full unpaginated count)
 *   - totalPages      (number, server-computed)
 *   - currentCount    (number, rows actually rendered on this page)
 *   - onPageChange    (page: number) => void
 *   - onPerPageChange (perPage: number) => void
 *
 * Notes
 *   - Pure presentational; parent owns the state.
 *   - All controls are disabled while there is exactly one page.
 *   - Per-page selector uses a native <select> (zero deps, accessible).
 */
import React from 'react';
import { Button } from '../ui/button';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const PER_PAGE_OPTIONS = [25, 50, 100];

const formatRange = (page, perPage, currentCount, totalCount) => {
  if (totalCount === 0) return '0 records';
  const start = (page - 1) * perPage + 1;
  const end = Math.min(start + currentCount - 1, totalCount);
  return `${start.toLocaleString()}–${end.toLocaleString()} of ${totalCount.toLocaleString()} records`;
};

const QCPagination = ({
  page,
  perPage,
  totalCount,
  totalPages,
  currentCount,
  onPageChange,
  onPerPageChange,
}) => {
  const isOnlyPage = totalPages <= 1;
  const canPrev = page > 1;
  const canNext = page < totalPages;

  return (
    <div
      className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 px-4 py-3 border-t border-slate-200 bg-slate-50/60"
      data-testid="qc-pagination"
    >
      <div className="text-sm text-slate-600" data-testid="qc-pagination-summary">
        Showing <span className="font-medium text-slate-800">{formatRange(page, perPage, currentCount, totalCount)}</span>
        <span className="mx-2 text-slate-300">·</span>
        Page <span className="font-medium text-slate-800">{page}</span> of <span className="font-medium text-slate-800">{totalPages}</span>
      </div>

      <div className="flex items-center gap-3">
        <label className="flex items-center gap-2 text-sm text-slate-600">
          <span>Rows per page:</span>
          <select
            value={perPage}
            onChange={(e) => onPerPageChange(Number(e.target.value))}
            className="h-8 rounded-md border border-slate-300 bg-white px-2 text-sm text-slate-700 hover:border-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
            disabled={totalCount === 0}
            data-testid="qc-pagination-per-page"
          >
            {PER_PAGE_OPTIONS.map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </label>

        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="sm"
            className="h-8 px-3"
            disabled={!canPrev || isOnlyPage}
            onClick={() => onPageChange(page - 1)}
            data-testid="qc-pagination-prev"
          >
            <ChevronLeft className="w-4 h-4 mr-1" /> Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-8 px-3"
            disabled={!canNext || isOnlyPage}
            onClick={() => onPageChange(page + 1)}
            data-testid="qc-pagination-next"
          >
            Next <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default QCPagination;
